<!-- Diálogo não modal (open, sem showModal): a plataforma continua utilizável com o painel aberto -->
<dialog
  id="onboarding-panel"
  open
  class="panel fixed bottom-0 left-auto right-0 top-0 z-[1300] m-0 flex h-auto max-h-none w-full max-w-none flex-col p-0 sm:w-[26rem]"
  tabindex="-1"
  aria-modal="false"
  aria-labelledby="onboarding-panel-title"
  (keydown.escape)="close()"
>
  <header class="panel-header flex flex-col gap-3 px-4 pb-3 pt-4">
    <div class="flex items-start gap-3">
      <div class="flex min-w-0 flex-1 flex-col gap-1">
        <span class="panel-kicker">{{ 'onboarding.panel.kicker' | translate }}</span>
        <h2 id="onboarding-panel-title" #heading tabindex="-1" class="panel-title">
          {{ (store.definition()?.titleKey ?? 'onboarding.panel.kicker') | translate }}
        </h2>
        @if (store.definition(); as definition) {
          <p class="panel-subtitle">{{ definition.subtitleKey | translate }}</p>
        }
      </div>
      <button
        id="onboarding-panel-close-button"
        type="button"
        class="icon-button inline-flex shrink-0 items-center justify-center rounded-full"
        [attr.aria-label]="'onboarding.panel.minimize' | translate"
        (click)="close()"
      >
        <mat-icon aria-hidden="true">close</mat-icon>
      </button>
    </div>

    @if (store.state(); as state) {
      <div class="flex flex-col gap-1">
        <mat-progress-bar
          id="onboarding-panel-progress"
          class="onboarding-progress-bar"
          mode="determinate"
          [value]="store.progressPercent()"
          [attr.aria-valuetext]="'onboarding.panel.progress' | translate: progressLabel()"
          [attr.aria-label]="'onboarding.panel.progress_label' | translate"
        ></mat-progress-bar>
        <span class="panel-meta">
          {{ 'onboarding.panel.progress' | translate: progressLabel() }}
          @if (state.optionalTotal > 0) {
            · {{ 'onboarding.panel.extras' | translate: { done: state.optionalCompleted, total: state.optionalTotal } }}
          }
        </span>
        @if (store.isReview() && state.status !== 'COMPLETED') {
          <p id="onboarding-panel-review-note" class="review-note flex items-start gap-2 rounded-lg px-3 py-2">
            <mat-icon aria-hidden="true">replay</mat-icon>
            <span>{{ 'onboarding.panel.review_note' | translate }}</span>
          </p>
        }
      </div>
    }

    <div class="tabs flex gap-1 rounded-lg p-1" role="tablist" [attr.aria-label]="'onboarding.panel.tabs_label' | translate">
      @for (tab of tabs; track tab) {
        <button
          [id]="'onboarding-panel-tab-' + tab"
          type="button"
          role="tab"
          class="tab flex-1 rounded-md px-3 py-1.5"
          [class.tab--active]="store.panelTab() === tab"
          [attr.aria-selected]="store.panelTab() === tab"
          [attr.aria-controls]="'onboarding-panel-tabpanel-' + tab"
          [attr.tabindex]="store.panelTab() === tab ? 0 : -1"
          (click)="selectTab(tab)"
          (keydown)="onTabKeydown($event)"
        >
          {{ 'onboarding.panel.tabs.' + tab | translate }}
        </button>
      }
    </div>
  </header>

  @if (store.error()) {
    <output id="onboarding-panel-error" class="panel-error mx-4 flex items-start gap-2 rounded-lg px-3 py-2">
      <mat-icon aria-hidden="true">cloud_off</mat-icon>
      <span>{{ 'onboarding.panel.unavailable' | translate }}</span>
    </output>
  }

  <div
    [id]="'onboarding-panel-tabpanel-' + store.panelTab()"
    class="panel-body flex-1 overflow-y-auto px-4 py-3"
    role="tabpanel"
    [attr.aria-labelledby]="'onboarding-panel-tab-' + store.panelTab()"
  >
    @if (store.panelTab() === 'steps') {
      @if (store.celebrating()) {
        <app-onboarding-completion class="mb-3 block" (navigated)="closed.emit()" />
      }
      @if (store.state()) {
        <app-onboarding-checklist idPrefix="onboarding-panel-checklist" (navigated)="closed.emit()" />
      } @else if (!store.error()) {
        <div class="flex justify-center py-8">
          <mat-spinner diameter="28" [attr.aria-label]="'onboarding.common.loading' | translate"></mat-spinner>
        </div>
      }
    } @else {
      <app-onboarding-tutorial-catalog idPrefix="onboarding-panel-catalog" (navigated)="closed.emit()" />
    }
  </div>

  @if (store.state(); as state) {
    <footer class="panel-footer flex flex-col gap-2 px-4 py-3">
      <div class="flex items-center justify-between gap-2">
        <span id="onboarding-panel-checked-at" class="panel-meta" aria-live="polite">
          @if (store.loading()) {
            {{ 'onboarding.panel.checking' | translate }}
          } @else if (state.checkedAt) {
            {{ 'onboarding.panel.checked_at' | translate: { time: checkedAtTime() } }}
          }
        </span>
        @if (state.status !== 'COMPLETED') {
          <button id="onboarding-panel-refresh-button" type="button" class="btn-ghost btn-sm" [disabled]="store.loading()" (click)="store.refresh()">
            <mat-icon aria-hidden="true">refresh</mat-icon>
            {{ 'onboarding.step.check_now' | translate }}
          </button>
        }
      </div>
      <div class="flex flex-wrap gap-2">
        @switch (state.status) {
          @case ('NOT_STARTED') {
            <button id="onboarding-panel-start-button" type="button" class="btn-primary btn-sm flex-1 justify-center" [disabled]="!!store.pendingAction()" (click)="store.start()">
              {{ 'onboarding.panel.start' | translate }}
            </button>
          }
          @case ('PAUSED') {
            <button id="onboarding-panel-resume-button" type="button" class="btn-primary btn-sm flex-1 justify-center" [disabled]="!!store.pendingAction()" (click)="store.resume()">
              {{ 'onboarding.panel.resume' | translate }}
            </button>
          }
          @case ('IN_PROGRESS') {
            @if (store.isReview()) {
              <button id="onboarding-panel-finish-review-button" type="button" class="btn-primary btn-sm flex-1 justify-center" [disabled]="!!store.pendingAction()" (click)="store.finishReview()">
                {{ 'onboarding.panel.finish_review' | translate }}
              </button>
            }
            <button id="onboarding-panel-pause-button" type="button" class="btn-secondary btn-sm flex-1 justify-center" [disabled]="!!store.pendingAction()" (click)="store.pause()">
              {{ 'onboarding.panel.pause' | translate }}
            </button>
          }
          @case ('COMPLETED') {
            <button id="onboarding-panel-redo-button" type="button" class="btn-secondary btn-sm flex-1 justify-center" [disabled]="!!store.pendingAction()" (click)="store.redo()">
              <mat-icon aria-hidden="true">replay</mat-icon>
              {{ 'onboarding.panel.redo' | translate }}
            </button>
          }
        }
      </div>
    </footer>
  }
</dialog>
